import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Eye, Edit, Trash2, User, ArrowUpDown } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface Contato {
  id: string;
  nome: string;
  email?: string;
  telefone?: string;
  cargo?: string;
  observacoes?: string;
  cliente_id?: string;
  local_id?: string;
  id_empresa?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  clientes?: { nome: string };
  locais?: { nome: string };
  empresas?: { razao_social: string };
}

interface Cliente {
  id: string;
  nome: string;
}

interface Local {
  id: string;
  nome: string;
}

interface Empresa {
  id: string;
  razao_social: string;
}

export default function Contatos() {
  const { user } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  
  // Estados principais
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("nome");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  
  // Estados dos modais
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedContato, setSelectedContato] = useState<Contato | null>(null);
  
  // Estado do formulário
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    cargo: "",
    observacoes: "",
    cliente_id: "none",
    local_id: "",
    id_empresa: "none",
    ativo: true
  });

  // Permissões
  const canRead = hasPermission('clientes', 'contatos', 'read');
  const canWrite = hasPermission('clientes', 'contatos', 'write');
  const canDelete = hasPermission('clientes', 'contatos', 'admin');

  // Carregamento inicial dos dados
  useEffect(() => {
    if (!permissionsLoading && canRead) {
      fetchData();
    } else if (!permissionsLoading && !canRead) {
      setLoadingTable(false);
    }
  }, [permissionsLoading, canRead]);

  const fetchData = async () => {
    try {
      setLoadingTable(true);
      
      // Buscar contatos
      const { data: contatosData, error: contatosError } = await supabase
        .from('contatos')
        .select(`
          *,
          clientes:cliente_id(nome),
          locais:local_id(nome),
          empresas:id_empresa(razao_social)
        `)
        .order(sortField, { ascending: sortOrder === "asc" });

      if (contatosError) throw contatosError;

      // Buscar clientes
      // @ts-ignore - Temporary fix for Supabase type inference issue
      const { data: clientesData, error: clientesError }: { data: any, error: any } = await supabase
        .from('clientes')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');

      if (clientesError) throw clientesError;

      // Buscar locais
      // @ts-ignore - Temporary fix for deep type instantiation
      const { data: locaisData, error: locaisError } = await supabase
        .from('locais')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');

      if (locaisError) throw locaisError;

      // Buscar empresas
      // @ts-ignore - Temporary fix for Supabase type inference issue
      const { data: empresasData, error: empresasError }: { data: any, error: any } = await supabase
        .from('empresas')
        .select('id, razao_social')
        .eq('ativo', true)
        .order('razao_social');

      if (empresasError) throw empresasError;

      setContatos((contatosData as any) || []);
      setClientes(clientesData || []);
      setLocais(locaisData || []);
      setEmpresas(empresasData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados",
        variant: "destructive",
      });
    } finally {
      setLoadingTable(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      email: "",
      telefone: "",
      cargo: "",
      observacoes: "",
      cliente_id: "none",
      local_id: "",
      id_empresa: "none",
      ativo: true
    });
    setSelectedContato(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nome) {
      toast({
        title: "Erro",
        description: "O nome é obrigatório",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const dataToSubmit = {
        nome: formData.nome,
        email: formData.email || null,
        telefone: formData.telefone || null,
        cargo: formData.cargo || null,
        observacoes: formData.observacoes || null,
        cliente_id: formData.cliente_id === "none" ? null : formData.cliente_id,
        local_id: formData.local_id || null,
        id_empresa: formData.id_empresa === "none" ? null : formData.id_empresa,
        ativo: formData.ativo,
        created_by: user?.id,
        updated_by: user?.id
      };

      if (selectedContato) {
        // Update
        const { error } = await supabase
          .from('contatos')
          .update(dataToSubmit)
          .eq('id', selectedContato.id);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Contato atualizado com sucesso",
        });
        setIsEditModalOpen(false);
      } else {
        // Create
        const { error } = await supabase
          .from('contatos')
          .insert([dataToSubmit]);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Contato criado com sucesso",
        });
        setIsCreateModalOpen(false);
      }

      fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving contato:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar contato",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este contato?")) return;

    try {
      const { error } = await supabase
        .from('contatos')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Contato excluído com sucesso",
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting contato:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir contato",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (contato: Contato) => {
    setSelectedContato(contato);
    setFormData({
      nome: contato.nome,
      email: contato.email || "",
      telefone: contato.telefone || "",
      cargo: contato.cargo || "",
      observacoes: contato.observacoes || "",
      cliente_id: contato.cliente_id || "none",
      local_id: contato.local_id || "",
      id_empresa: contato.id_empresa || "none",
      ativo: contato.ativo
    });
    setIsEditModalOpen(true);
  };

  const handleView = (contato: Contato) => {
    setSelectedContato(contato);
    setIsViewModalOpen(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    fetchData();
  };

  // Filtros
  const filteredContatos = contatos.filter(contato =>
    contato.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contato.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contato.cargo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contato.clientes?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contato.locais?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contato.empresas?.razao_social?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Formulário modal
  const ModalForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="nome">Nome *</Label>
          <Input
            id="nome"
            value={formData.nome}
            onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
            required
          />
        </div>
        
        <div>
          <Label htmlFor="email">E-mail</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="telefone">Telefone</Label>
          <Input
            id="telefone"
            value={formData.telefone}
            onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
          />
        </div>
        
        <div>
          <Label htmlFor="cargo">Cargo</Label>
          <Input
            id="cargo"
            value={formData.cargo}
            onChange={(e) => setFormData(prev => ({ ...prev, cargo: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="cliente_id">Cliente (opcional)</Label>
          <Select 
            value={formData.cliente_id} 
            onValueChange={(value) => setFormData(prev => ({ ...prev, cliente_id: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione um cliente (opcional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhum cliente</SelectItem>
              {clientes.map((cliente) => (
                <SelectItem key={cliente.id} value={cliente.id}>
                  {cliente.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="local_id">Local (opcional)</Label>
          <Select 
            value={formData.local_id} 
            onValueChange={(value) => setFormData(prev => ({ ...prev, local_id: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione um local (opcional)" />
            </SelectTrigger>
            <SelectContent>
              {locais.map((local) => (
                <SelectItem key={local.id} value={local.id}>
                  {local.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="id_empresa">Empresa (opcional)</Label>
          <Select 
            value={formData.id_empresa} 
            onValueChange={(value) => setFormData(prev => ({ ...prev, id_empresa: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione uma empresa (opcional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhuma empresa</SelectItem>
              {empresas.map((empresa) => (
                <SelectItem key={empresa.id} value={empresa.id}>
                  {empresa.razao_social}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="observacoes">Observações</Label>
        <Textarea
          id="observacoes"
          value={formData.observacoes}
          onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
          rows={3}
        />
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={() => {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          resetForm();
        }}>
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? "Salvando..." : selectedContato ? "Atualizar" : "Criar"}
        </Button>
      </div>
    </form>
  );

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2">Carregando permissões...</p>
        </div>
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Acesso Negado</h2>
          <p>Você não tem permissão para acessar esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Contatos</h1>
          <p className="text-muted-foreground">Gerencie os contatos cadastrados</p>
        </div>
        {canWrite && (
          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                Novo Contato
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Criar Novo Contato</DialogTitle>
              </DialogHeader>
              <ModalForm />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Lista de Contatos</CardTitle>
            <div className="flex items-center space-x-2">
              <Input
                placeholder="Buscar contatos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingTable ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2">Carregando contatos...</span>
            </div>
          ) : filteredContatos.length === 0 ? (
            <div className="text-center py-8">
              <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Nenhum contato encontrado</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? "Tente ajustar os filtros de busca" : "Comece criando um novo contato"}
              </p>
              {canWrite && !searchTerm && (
                <Button onClick={() => setIsCreateModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Contato
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Button variant="ghost" onClick={() => handleSort('nome')}>
                      Nome <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContatos.map((contato) => (
                  <TableRow key={contato.id}>
                    <TableCell className="font-medium">{contato.nome}</TableCell>
                    <TableCell>{contato.email || '-'}</TableCell>
                    <TableCell>{contato.telefone || '-'}</TableCell>
                    <TableCell>{contato.cargo || '-'}</TableCell>
                    <TableCell>{contato.clientes?.nome || '-'}</TableCell>
                    <TableCell>{contato.locais?.nome || '-'}</TableCell>
                    <TableCell>{contato.empresas?.razao_social || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={contato.ativo ? "default" : "secondary"}>
                        {contato.ativo ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleView(contato)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canWrite && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(contato)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(contato.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal de Edição */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Contato</DialogTitle>
          </DialogHeader>
          <ModalForm />
        </DialogContent>
      </Dialog>

      {/* Modal de Visualização */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Contato</DialogTitle>
          </DialogHeader>
          {selectedContato && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Nome:</Label>
                  <p>{selectedContato.nome}</p>
                </div>
                <div>
                  <Label className="font-semibold">Status:</Label>
                  <Badge variant={selectedContato.ativo ? "default" : "secondary"}>
                    {selectedContato.ativo ? "Ativo" : "Inativo"}
                  </Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">E-mail:</Label>
                  <p>{selectedContato.email || '-'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Telefone:</Label>
                  <p>{selectedContato.telefone || '-'}</p>
                </div>
              </div>
              
              <div>
                <Label className="font-semibold">Cargo:</Label>
                <p>{selectedContato.cargo || '-'}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="font-semibold">Cliente:</Label>
                  <p>{selectedContato.clientes?.nome || '-'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Local:</Label>
                  <p>{selectedContato.locais?.nome || '-'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Empresa:</Label>
                  <p>{selectedContato.empresas?.razao_social || '-'}</p>
                </div>
              </div>
              
              {selectedContato.observacoes && (
                <div>
                  <Label className="font-semibold">Observações:</Label>
                  <p className="mt-1 p-2 bg-muted rounded-md">{selectedContato.observacoes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}